import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Compass, MessageCircle, Wallet as WalletIcon, LogOut, User, Shield, ShieldCheck, Home, Heart, Sparkles, Users, Store, MoreHorizontal, X, HeartHandshake, Bell, BellOff, BellRing, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO } from "../lib/constants";
import api, { fileUrl } from "../lib/api";
import { enablePush, disablePush, pushIsEnabled, pushSupported } from "../lib/push";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import TipModal from "./TipModal";
import NotificationBell from "./NotificationBell";
import RecapModal from "./RecapModal";
import SupporterOnboarding from "./SupporterOnboarding";
import WelcomeGate from "./WelcomeGate";
import InstallPWA from "./InstallPWA";
import NotifyPrompt from "./NotifyPrompt";
import NotifPrefsDialog from "./NotifPrefsDialog";
import ChristmasDecor, { LightsGarland } from "./ChristmasDecor";

// A clearly identifiable copper penny with a warm heart — on brand, never lost on white.
const PennyCoin = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="#CE8A2B" stroke="#8C520F" strokeWidth="1.6" />
    <circle cx="12" cy="12" r="6.8" fill="none" stroke="#F4C77C" strokeWidth="0.9" opacity="0.6" />
    <path d="M12 15.4c-2.5-1.6-3.7-2.9-3.7-4.4 0-1 .8-1.8 1.8-1.8.6 0 1.2.3 1.6.8l.3.4.3-.4c.4-.5 1-.8 1.6-.8 1 0 1.8.8 1.8 1.8 0 1.5-1.2 2.8-3.7 4.4z" fill="#F7E3B0" />
  </svg>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jar, setJar] = useState(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [christmas, setChristmas] = useState(false);

  const loadJar = () => api.get("/jar").then((r) => setJar(r.data)).catch(() => {});
  useEffect(() => { loadJar(); }, []);
  useEffect(() => { api.get("/settings/theme").then((r) => setChristmas(!!r.data.christmas)).catch(() => {}); }, []);
  useEffect(() => { if (user) pushIsEnabled().then(setPushOn); }, [user]);

  const togglePush = async () => {
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success("Notifications turned off"); }
      else {
        const ok = await enablePush();
        setPushOn(ok);
        toast[ok ? "success" : "message"](ok ? "Notifications on 💛" : "Allow notifications in your browser to enable.");
      }
    } catch (e) { toast.error("Notifications aren't available on this device."); }
  };

  const isMod = !!(user && (user.is_guardian || user.role === "admin"));

  const navItems = [
    { to: "/", label: "Home", icon: Home, always: true },
    { to: "/discover", label: "Discover", icon: Compass, always: true },
    { to: "/sponsor", label: "Sponsor", icon: Gift, always: true },
    { to: "/feed", label: "Timeline", icon: Heart, auth: true },
    { to: "/community", label: "Community", icon: Users, auth: true },
    { to: "/neighbors", label: "Neighbors", icon: HeartHandshake, auth: true },
    { to: "/messages", label: "Messages", icon: MessageCircle, auth: true },
    { to: "/shop", label: "Shop", icon: Store, always: true },
    { to: "/wallet", label: "Wallet", icon: WalletIcon, auth: true },
    { to: "/guardian", label: "Guardian", icon: ShieldCheck, mod: true },
  ];

  const visibleNav = navItems.filter((i) => i.always || (i.auth && user) || (i.mod && isMod));

  // Up to 4 primary destinations for the mobile bottom bar; rest live under "More".
  const bottomWants = user ? ["/feed", "/discover", "/community", "/wallet"] : ["/", "/discover", "/shop"];
  const bottomItems = bottomWants.map((t) => visibleNav.find((i) => i.to === t)).filter(Boolean);

  return (
    <div className="min-h-screen bg-background paper-bg">
      <header data-testid="main-header" className="sticky top-0 z-50 glass-nav border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-4 relative">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
            <img src={LOGO} alt="HAPLAP" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-cover shadow-sm" />
            <span className="font-serif font-bold text-xl text-bronze hidden sm:block tracking-tight">HAPLAP</span>
          </Link>

          {/* Perfectly centered balance badge (mobile) */}
          <button data-testid="jar-badge-mobile" onClick={() => setTipOpen(true)}
            className="md:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 bg-penny/15 hover:bg-penny/25 text-bronze px-3 py-1.5 rounded-full transition-colors min-h-[40px]">
            <PennyCoin className="w-5 h-5" />
            <span className="font-mono text-sm font-bold">${jar ? jar.balance.toFixed(2) : "—"}</span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {visibleNav.map((i) => (
              <NavLink key={i.to} to={i.to} data-testid={`nav-${i.label.toLowerCase()}`} title={i.label}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "bg-copper/15 text-copper" : "text-foreground/70 hover:bg-secondary"
                  }`}>
                <i.icon className="w-4 h-4 shrink-0" /> <span className="hidden 2xl:inline">{i.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button data-testid="jar-badge" onClick={() => setTipOpen(true)}
              className="hidden md:flex items-center gap-2 bg-penny/15 hover:bg-penny/25 text-bronze px-3 py-1.5 rounded-full transition-colors min-h-[40px]">
              <PennyCoin className="w-5 h-5" />
              <span className="font-mono text-sm font-bold">${jar ? jar.balance.toFixed(2) : "—"}</span>
            </button>

            {user ? (
              <>
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-secondary transition-colors">
                      <img src={fileUrl(user.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${user.user_id}`}
                        alt="me" className="w-8 h-8 rounded-full object-cover bg-secondary" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 bg-white border border-border shadow-lg">
                    <div className="px-2 py-1.5 text-sm">
                      <p className="font-semibold truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-my-profile" onClick={() => navigate(`/u/${user.user_id}`)}>
                      <User className="w-4 h-4 mr-2" /> My Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-recap" onClick={() => setRecapOpen(true)}>
                      <Sparkles className="w-4 h-4 mr-2" /> Weekly Recap
                    </DropdownMenuItem>
                    {pushSupported() && (
                      <DropdownMenuItem data-testid="menu-notifications" onClick={togglePush}>
                        {pushOn ? <BellOff className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                        {pushOn ? "Turn off notifications" : "Turn on notifications"}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem data-testid="menu-notif-settings" onClick={() => setPrefsOpen(true)}>
                      <BellRing className="w-4 h-4 mr-2" /> Notification settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/wallet")}>
                      <WalletIcon className="w-4 h-4 mr-2" /> Wallet
                    </DropdownMenuItem>
                    {isMod && (
                      <DropdownMenuItem data-testid="menu-guardian" onClick={() => navigate("/guardian")}>
                        <ShieldCheck className="w-4 h-4 mr-2" /> Guardian feed
                      </DropdownMenuItem>
                    )}
                    {user.role === "admin" && (
                      <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                        <Shield className="w-4 h-4 mr-2" /> Moderation
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button data-testid="header-signin-btn" onClick={() => navigate("/login")}
                className="bg-copper hover:bg-copper/90 text-white rounded-full min-h-[40px]">Sign in</Button>
            )}
          </div>
        </div>
      </header>
      {christmas && <LightsGarland />}

      <main className="pb-20 md:pb-0"><Outlet /></main>

      <footer data-testid="site-footer" className="border-t border-border mt-0 bg-cream/40">
        <div className="max-w-5xl mx-auto px-4 py-12 pb-24 md:pb-12 flex flex-col items-center text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={LOGO} alt="" className="w-7 h-7 rounded-md" />
            <span className="font-serif font-semibold text-lg text-bronze">HAPLAP</span>
          </div>
          <p className="max-w-md mx-auto px-4 font-serif italic text-sm text-foreground/70">
            "Have a penny, leave a penny. Need a penny, take a penny."
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-16 mt-9 mb-8 text-left mx-auto">
            <div className="flex flex-col items-center sm:items-start gap-2.5">
              <h4 className="font-mono text-xs font-semibold uppercase tracking-widest text-copper">Company</h4>
              <Link to="/about" data-testid="footer-about" className="text-sm text-foreground/70 hover:text-copper transition-colors">Our Story</Link>
              <Link to="/about" data-testid="footer-mission" className="text-sm text-foreground/70 hover:text-copper transition-colors">Mission</Link>
              <Link to="/discover" data-testid="footer-discover" className="text-sm text-foreground/70 hover:text-copper transition-colors">Discover</Link>
            </div>
            <div className="flex flex-col items-center sm:items-start gap-2.5">
              <h4 className="font-mono text-xs font-semibold uppercase tracking-widest text-copper">Legal</h4>
              <Link to="/privacy" data-testid="footer-privacy" className="text-sm text-foreground/70 hover:text-copper transition-colors">Privacy Policy</Link>
              <Link to="/terms" data-testid="footer-terms" className="text-sm text-foreground/70 hover:text-copper transition-colors">Terms of Service</Link>
              <Link to="/refund" data-testid="footer-refund" className="text-sm text-foreground/70 hover:text-copper transition-colors">Refund Policy</Link>
            </div>
            <div className="flex flex-col items-center sm:items-start gap-2.5 col-span-2 sm:col-span-1">
              <h4 className="font-mono text-xs font-semibold uppercase tracking-widest text-copper">Support</h4>
              <Link to="/faq" data-testid="footer-faq" className="text-sm text-foreground/70 hover:text-copper transition-colors">FAQ</Link>
              <Link to="/contact" data-testid="footer-contact" className="text-sm text-foreground/70 hover:text-copper transition-colors">Contact Us</Link>
              <a href="mailto:support@haplap.ca" data-testid="footer-support-email" className="text-sm text-foreground/70 hover:text-copper transition-colors">support@haplap.ca</a>
              <button type="button" data-testid="footer-install" onClick={() => window.dispatchEvent(new Event("haplap:install"))} className="text-sm text-foreground/70 hover:text-copper transition-colors text-center sm:text-left">Install the app</button>
            </div>
          </div>

          <div className="w-full border-t border-border/60 pt-6 flex flex-col items-center gap-1.5">
            <p className="text-xs text-muted-foreground">A dignity-first community. Zero tolerance for harassment. 💛</p>
            <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Haplap · Made with heart in Newfoundland, Canada</p>
          </div>
        </div>
      </footer>

      {/* Mobile bottom tab bar */}
      <nav data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-lg border-t border-border flex items-stretch justify-around h-16 px-1 pb-[env(safe-area-inset-bottom,0px)]">
        {bottomItems.map((i) => (
          <NavLink key={i.to} to={i.to} data-testid={`tab-${i.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 flex-1 min-h-[44px] text-[10px] font-medium transition-colors ${
                isActive ? "text-copper" : "text-foreground/60"
              }`}>
            <i.icon className="w-5 h-5" /> {i.label}
          </NavLink>
        ))}
        <button data-testid="tab-more" onClick={() => setMoreOpen(true)}
          className="flex flex-col items-center justify-center gap-0.5 flex-1 min-h-[44px] text-[10px] font-medium text-foreground/60">
          <MoreHorizontal className="w-5 h-5" /> More
        </button>
      </nav>

      {/* Mobile "More" bottom sheet */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-black/40 animate-in fade-in duration-150" />
          <div onClick={(e) => e.stopPropagation()}
            className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl border-t border-border p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-1 animate-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between px-1 pb-2">
              <span className="font-serif font-semibold text-bronze">Menu</span>
              <button data-testid="more-close" onClick={() => setMoreOpen(false)} className="p-2 rounded-full hover:bg-secondary"><X className="w-4 h-4" /></button>
            </div>
            {visibleNav.map((i) => (
              <NavLink key={i.to} to={i.to} onClick={() => setMoreOpen(false)} data-testid={`more-${i.label.toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium min-h-[48px] ${isActive ? "bg-copper/15 text-copper" : "text-foreground/80 hover:bg-secondary"}`}>
                <i.icon className="w-4 h-4" /> {i.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink to="/admin" onClick={() => setMoreOpen(false)} data-testid="more-admin"
                className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium min-h-[48px] ${isActive ? "bg-copper/15 text-copper" : "text-foreground/80 hover:bg-secondary"}`}>
                <Shield className="w-4 h-4" /> Moderation
              </NavLink>
            )}
          </div>
        </div>
      )}

      <TipModal open={tipOpen} onClose={() => { setTipOpen(false); loadJar(); }} mode="community" />
      <RecapModal open={recapOpen} onClose={() => setRecapOpen(false)} />
      <SupporterOnboarding />
      <WelcomeGate />
      <InstallPWA />
      <NotifyPrompt />
      <NotifPrefsDialog open={prefsOpen} onOpenChange={setPrefsOpen} />
      {christmas && <ChristmasDecor />}
    </div>
  );
}
